/**
 * SessionCrudService - Session CRUD operations with optimistic updates
 *
 * Handles session create/read/update/delete operations using optimistic updates
 * with backend sync and rollback on error.
 *
 * Extracted from AgentCoreAdapter to reduce adapter complexity.
 *
 * @example
 * const service = new SessionCrudService({
 *   sessionManager,
 *   messageManager,
 *   backendApi: { updateAgentSession, deleteAgentSession },
 *   onMutation: () => this._markRecentMutation(),
 *   onRollback: () => this._clearMutationFlag()
 * });
 *
 * await service.updateSession('session-1', { name: 'New Name' });
 * await service.deleteSession('session-1');
 */

// Fix #20: Allowed fields for session updates - unknown fields are rejected
const ALLOWED_UPDATE_FIELDS = new Set(['name', 'is_favorite', 'is_archived']);

class SessionCrudService {
  /**
   * @param {Object} options
   * @param {Object} options.sessionManager - Session state manager
   * @param {Object} options.messageManager - Message state manager
   * @param {Object} options.backendApi - Backend API functions { updateAgentSession, deleteAgentSession }
   * @param {Function} [options.onMutation] - Callback when mutation occurs (for cache invalidation)
   * @param {Function} [options.onRollback] - Callback when rollback occurs (Fix #22: to reset mutation flag)
   */
  constructor({ sessionManager, messageManager, backendApi, onMutation, onRollback } = {}) {
    if (!sessionManager) {
      throw new Error('sessionManager is required');
    }
    if (!messageManager) {
      throw new Error('messageManager is required');
    }
    if (!backendApi) {
      throw new Error('backendApi is required');
    }

    this._sessionManager = sessionManager;
    this._messageManager = messageManager;
    this._backendApi = backendApi;
    this._onMutation = onMutation;
    // Fix #22: Optional rollback callback to reset mutation flag
    this._onRollback = onRollback;
  }

  /**
   * Update session metadata with optimistic update and backend sync.
   * Rolls back to original state on backend error.
   *
   * @param {string} sessionId - Session to update
   * @param {Object} updates - Fields to update { name?, is_favorite?, is_archived? }
   * @returns {Promise<Object>} Updated session
   * @throws {Error} If session not found, invalid fields, or backend fails
   */
  async updateSession(sessionId, updates) {
    // Fix #20: Validate allowed fields before processing
    const unknownFields = Object.keys(updates).filter(
      (key) => !ALLOWED_UPDATE_FIELDS.has(key)
    );
    if (unknownFields.length > 0) {
      throw new Error(`Unknown update fields: ${unknownFields.join(', ')}`);
    }

    const session = this._sessionManager.getById(sessionId);
    if (!session) {
      throw new Error(`Session not found: ${sessionId}`);
    }

    // Save original for rollback
    const originalSession = { ...session };

    // Apply optimistic updates with validation
    if (updates.name !== undefined) {
      // Fix #25: Validate update values are not null/empty
      if (updates.name === null || (typeof updates.name === 'string' && updates.name.trim() === '')) {
        throw new Error('Invalid session name: cannot be null or empty');
      }
      session.title = typeof updates.name === 'string' ? updates.name.trim() : updates.name;
    }
    if (updates.is_favorite !== undefined) {
      session.isFavorite = updates.is_favorite;
    }
    if (updates.is_archived !== undefined) {
      session.isArchived = updates.is_archived;
    }

    // Persist optimistic update
    this._sessionManager.set(sessionId, session);
    this._sessionManager.save();
    this._notifyMutation();

    try {
      // Sync with backend
      const response = await this._backendApi.updateAgentSession(sessionId, updates);

      // Apply backend response (source of truth)
      if (response) {
        // Fix #28: Check timestamp to prevent stale backend data from overwriting newer local state
        const responseUpdatedAt = response.updated_at ? new Date(response.updated_at) : null;
        const currentLastActivity = session.lastActivity instanceof Date ? session.lastActivity : null;

        // Only apply backend response if it's newer or we don't have a local timestamp
        const shouldApplyResponse = !currentLastActivity || !responseUpdatedAt ||
          responseUpdatedAt >= currentLastActivity;

        if (shouldApplyResponse) {
          session.title = response.name || session.title;
          // Fix #7: Use ?? to preserve explicit false values from backend (not || which treats false as falsy)
          session.isFavorite = response.is_favorite ?? session.isFavorite;
          session.isArchived = response.is_archived ?? session.isArchived;
          if (response.updated_at) {
            session.lastActivity = new Date(response.updated_at);
          }
          this._sessionManager.set(sessionId, session);
          this._sessionManager.save();
        }
      }

      return session;
    } catch (error) {
      // Rollback on error
      this._sessionManager.set(sessionId, originalSession);
      this._sessionManager.save();
      // Fix #22: Notify rollback to reset mutation flag
      this._notifyRollback();
      // Fix #16: Log full error internally, throw sanitized message
      console.error('SessionCrudService: Update failed:', error);
      // Fix #3: Preserve error stack with { cause } for debugging while sanitizing message
      throw new Error('Failed to update session', { cause: error });
    }
  }

  /**
   * Delete session with optimistic update and backend sync.
   * Rolls back to original state on backend error.
   *
   * @param {string} sessionId - Session to delete
   * @returns {Promise<void>}
   * @throws {Error} If session not found or backend fails
   */
  async deleteSession(sessionId) {
    const session = this._sessionManager.getById(sessionId);
    if (!session) {
      throw new Error(`Session not found: ${sessionId}`);
    }

    // Save original for rollback
    const originalSession = session;
    const originalMessages = this._messageManager.getForSession(sessionId);

    // Apply optimistic delete
    this._sessionManager.deleteById(sessionId);
    this._messageManager.deleteForSession(sessionId);
    this._notifyMutation();

    try {
      // Sync with backend
      await this._backendApi.deleteAgentSession(sessionId);
    } catch (error) {
      // Rollback on error
      this._sessionManager.set(sessionId, originalSession);
      if (originalMessages.length > 0) {
        this._messageManager.setForSession(sessionId, originalMessages);
      }
      // Fix #22: Notify rollback to reset mutation flag
      this._notifyRollback();
      // Fix #16: Log full error internally, throw sanitized message
      console.error('SessionCrudService: Delete failed:', error);
      // Fix #4: Preserve error stack with { cause } for debugging while sanitizing message
      throw new Error('Failed to delete session', { cause: error });
    }
  }

  /**
   * Touch session to update lastActivity timestamp (local only).
   *
   * @param {string} sessionId - Session to touch
   * @returns {Object|null} Updated session or null if not found
   */
  touchSession(sessionId) {
    const session = this._sessionManager.getById(sessionId);
    if (!session) {
      return null;
    }

    session.lastActivity = new Date();
    this._sessionManager.set(sessionId, session);
    this._sessionManager.save();

    return session;
  }

  /**
   * Notify mutation callback if provided.
   * @private
   */
  _notifyMutation() {
    if (typeof this._onMutation === 'function') {
      this._onMutation();
    }
  }

  /**
   * Notify rollback callback if provided.
   * Fix #22: Called during rollback to allow adapter to reset mutation flag.
   * @private
   */
  _notifyRollback() {
    if (typeof this._onRollback === 'function') {
      this._onRollback();
    }
  }
}

export default SessionCrudService;
